import { Box, IconButton, Tooltip } from "@mui/material";
import { MdsContentCopyXs } from "@sinch/icons";
import { useCopyToClipboard } from "@sinch/hooks/useCopyToClipboard";
import { useSnackbar } from "notistack";
import React, { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components/MdsIcon/MdsIcon";

export interface CopyToClipboardProps {
  /**
   * The text written to the clipboard when the copy icon is pressed.
   */
  value: string;
  /**
   * The wrapped content. It stays fully interactive - the copy icon is rendered alongside it.
   */
  children: ReactNode;
  /**
   * Overrides the default success toast message.
   */
  successMessage?: string;
  /**
   * Overrides the default icon tooltip / accessible label.
   */
  tooltip?: string;
  /**
   * When `true` the copy icon is always visible. When `false` (default) it is hidden and only
   * revealed on hover/focus on pointer devices (it stays visible on touch devices regardless).
   */
  alwaysVisible?: boolean;
}

/**
 * Wraps any content with a copy-to-clipboard affordance. A copy icon sits next to the content:
 * by default it fades in on hover/focus on pointer devices and is permanently visible on touch
 * devices (where hover does not exist), or always visible when `alwaysVisible` is set. Pressing
 * it copies `value` and confirms with a toast.
 */
export const CopyToClipboard: FC<CopyToClipboardProps> = ({
  value,
  children,
  successMessage,
  tooltip,
  alwaysVisible = false,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { copy } = useCopyToClipboard();

  const handleCopy = async (): Promise<void> => {
    const success = await copy(value);
    enqueueSnackbar(success ? (successMessage ?? t("copiedToClipboard")) : t("copyFailed"), {
      variant: success ? "success" : "error",
    });
  };

  const label = tooltip ?? t("copyToClipboard");

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        "& .CopyToClipboard-button": {
          opacity: alwaysVisible ? 0.5 : 0,
          transition: "opacity 0.15s ease-in-out",
          "&:hover": {
            opacity: 1,
          },
        },
        ...(!alwaysVisible && {
          "&:hover .CopyToClipboard-button, &:focus-within .CopyToClipboard-button": {
            opacity: 0.5,
          },
          "@media (hover: none)": {
            "& .CopyToClipboard-button": {
              opacity: 0.5,
            },
          },
        }),
      }}
    >
      {children}
      <Tooltip enterTouchDelay={0} title={label}>
        <IconButton aria-label={label} className="CopyToClipboard-button" onClick={handleCopy} size="small">
          <MdsIcon fontSize="xs" icon={MdsContentCopyXs} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
